// MACHINERY V2 (Stroje) — V4 shop-floor wall: tile text atoms (M2.14).
//
// Split out of wallTileBody.tsx, which reached 330 lines once all six status
// branches were written — over the project's 300-line cap. The seam is the same
// one cardStateStyles.ts used for cardStates.ts: pure presentation that decides
// NOTHING. Everything here is layout; every branch that chooses WHAT to show
// stayed in wallTileBody.tsx.
//
// Sizes are the artboard's (desktop/screenshots/v4-wall--refined-claude.png and
// v4-wall--machineid-native.png, both viewed before this wave's code was
// written): section labels 10-11px mono, the huge readouts 28-30px mono.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

/** Section label. `.eyebrow` is the shipped token-sheet atom (mono, uppercase,
 *  0.1em, --faint, machinery-v2-tokens.scss:209-216); the artboard runs these a
 *  shade darker and a point larger than its default, hence the two overrides. */
export const Label: React.FC<{ text: string; size?: number }> = ({ text, size = 11 }) => (
  <div className="eyebrow" style={{ fontSize: size, color: "var(--ink-2)", opacity: 0.6 }}>
    {text}
  </div>
);

/** The huge mono readout — the thing that is legible from across the hall. */
export const BigValue: React.FC<{ text: string; color: string; size?: number }> = ({
  text,
  color,
  size = 30,
}) => (
  <div
    style={{
      fontFamily: "var(--font-mono)",
      fontSize: size,
      color,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      marginTop: 2,
      lineHeight: 1.05,
    }}
  >
    {text}
  </div>
);

export interface WallCell {
  label: string;
  value: string;
  /** Render as a BigValue in the status ink rather than as small mono. */
  big?: boolean;
  size?: number;
}

/**
 * The artboard's two-column tile footer (`1fr 1fr`, gap 14, bottom-aligned).
 *
 * RENDERS NOTHING AT ALL WHEN NEITHER CELL HAS A SOURCE VALUE. An empty but
 * labelled grid — "VRETENO · FEED" with no number under it, which is exactly
 * what the prototype artboard prints for its i2601 tile — is the sparse-tile
 * failure the data-honesty ruling is about. A caller passing two nulls gets an
 * absent footer, not an empty one.
 */
export const WallTileFooter: React.FC<{
  left: WallCell | null;
  right: WallCell | null;
  ink: string;
}> = ({ left, right, ink }) => {
  if (!left && !right) return null;

  const cell = (c: WallCell | null, align: "left" | "right") =>
    c ? (
      <div style={{ textAlign: align, minWidth: 0 }}>
        <Label text={c.label} size={10} />
        {c.big ? (
          <BigValue text={c.value} color={ink} size={c.size} />
        ) : (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: c.size || 15,
              color: "var(--ink)",
              marginTop: 2,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {c.value}
          </div>
        )}
      </div>
    ) : (
      <span />
    );

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 14,
        marginTop: 6,
        alignItems: "flex-end",
      }}
    >
      {cell(left, "left")}
      {cell(right, "right")}
    </div>
  );
};

/** The explainer block the three "nothing live to show" states share
 *  (unconnected, awaiting, and the no-data tail of everything else). */
export const WallExplainer: React.FC<{ label: string; text: string }> = ({ label, text }) => (
  <div>
    <Label text={label} />
    <div style={{ fontSize: 14, color: "var(--muted)", marginTop: 4, lineHeight: 1.35 }}>
      {text}
    </div>
  </div>
);
